import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Archive, ArchiveRestore, Loader2, X, Lock, Trash2, Boxes, ClipboardList } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useForm } from "../../components/forms/useForm";
import { Field, TextInput, SelectInput, Textarea } from "../../components/forms/Field";
import { required } from "../../lib/validation";
import { Toast, ConfirmDialog } from "./assets";
import EmptyState from "../../components/EmptyState";
import dict from "../../i18n/es-MX";

function Modal({ open, title, onClose, children, footer }) {
    if (!open) return null;
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="modal">
        <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
        <div className="relative w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-800">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
                <h2 className="font-display text-xl font-bold">{title}</h2>
                <button onClick={onClose} className="text-slate-400 p-1"><X size={18}/></button>
            </div>
            <div className="p-5">{children}</div>
            {footer && <div className="px-5 py-4 border-t border-slate-800 flex justify-end gap-2">{footer}</div>}
        </div>
    </div>;
}

// ============================================================
// MATERIALS
// ============================================================

export function MaterialsList() {
    const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); const [creating, setCreating] = useState(false);
    const [toast, setToast] = useState(null); const [confirm, setConfirm] = useState(null);
    const [archived, setArchived] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get("/materials",{params:archived?{archived:true}:{}}); setItems(r.data); }
        catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
        finally { setLoading(false); }
    }, [archived]);
    useEffect(() => { load(); }, [load]);

    const form = useForm({ initial:{name:"",unit:"m3",notes:""}, validators:{ name: required, unit: required } });

    const openCreate = () => { form.reset({name:"",unit:"m3",notes:""}); setCreating(true); setEditing(null); };
    const openEdit = (m) => { form.reset({name:m.name,unit:m.unit,notes:m.notes||""}); setEditing(m); setCreating(false); };
    const close = () => { setCreating(false); setEditing(null); };

    const onSubmit = (e) => { e.preventDefault();
        form.handleSubmit(async (v) => {
            const body = { ...v, notes: v.notes||null };
            try {
                if (editing) await api.patch(`/materials/${editing.id}`, body);
                else await api.post("/materials", body);
                setToast({msg:dict.common.saved,type:"success"}); close(); load();
            } catch (err) { setToast({msg:formatApiError(err),type:"error"}); }
        });
    };

    const doArchive = async (id, isArchived) => {
        try { await api.post(`/materials/${id}/${isArchived?"restore":"archive"}`); load(); }
        catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
        setConfirm(null);
    };

    return <div data-testid="materials-page" className="space-y-5">
        <header className="flex items-start justify-between flex-wrap gap-3">
            <div><h1 className="font-display text-4xl sm:text-5xl font-extrabold">{dict.materials.title}</h1><p className="mt-2 text-slate-400 text-sm">{dict.materials.subtitle}</p></div>
            <button onClick={openCreate} data-testid="new-material-button" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-brand-orange text-slate-950 font-semibold uppercase tracking-wider text-sm"><Plus size={18}/>{dict.materials.newMaterial}</button>
        </header>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={archived} onChange={e=>setArchived(e.target.checked)} data-testid="filter-archived"/>{dict.common.showArchived}</label>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="materials-table">
                <thead className="bg-slate-900/80 text-xs uppercase text-slate-400"><tr><th className="text-left px-4 py-3">{dict.materials.cols.name}</th><th className="text-left px-4 py-3">{dict.materials.cols.unit}</th><th className="text-left px-4 py-3">{dict.materials.cols.notes}</th><th className="text-right px-4 py-3">{dict.common.actions}</th></tr></thead>
                <tbody>
                    {loading && <tr><td colSpan={4} className="px-4 py-10 text-center"><Loader2 className="animate-spin inline"/></td></tr>}
                    {!loading && items.length===0 && <tr><td colSpan={4} className="px-0 py-0"><EmptyState icon={Boxes} title={dict.materials.empty} ctaLabel={dict.materials.newMaterial} onCta={openCreate} testid="materials-empty"/></td></tr>}
                    {!loading && items.map(m => <tr key={m.id} data-testid={`material-row-${m.id}`} className="border-t border-slate-800 hover:bg-slate-900/60">
                        <td className="px-4 py-3 text-slate-100">{m.name}</td>
                        <td className="px-4 py-3 text-slate-300">{dict.materials.units[m.unit]}</td>
                        <td className="px-4 py-3 text-slate-400 truncate max-w-xs">{m.notes||"—"}</td>
                        <td className="px-4 py-3"><div className="flex justify-end gap-1">
                            <button onClick={()=>openEdit(m)} data-testid={`edit-material-${m.id}`} className="p-2 rounded hover:bg-slate-800"><Pencil size={16}/></button>
                            <button onClick={()=>setConfirm({id:m.id,archived:m.archived,title:m.archived?dict.common.confirmRestore:dict.common.confirmArchive})} data-testid={`archive-material-${m.id}`} className={`p-2 rounded hover:bg-slate-800 ${m.archived?"text-emerald-400":"text-red-400"}`}>{m.archived?<ArchiveRestore size={16}/>:<Archive size={16}/>}</button>
                        </div></td>
                    </tr>)}
                </tbody>
            </table>
        </div>
        <Modal open={creating||!!editing} onClose={close} title={editing?dict.materials.editMaterial:dict.materials.newMaterial} footer={<>
            <button type="button" onClick={close} className="h-10 px-4 rounded-lg border border-slate-700 text-sm">{dict.common.cancel}</button>
            <button type="submit" form="mat-form" disabled={form.submitting} data-testid="material-submit" className="h-10 px-4 rounded-lg bg-brand-orange text-slate-950 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60">{form.submitting&&<Loader2 className="animate-spin" size={14}/>}{dict.common.save}</button>
        </>}>
            <form id="mat-form" onSubmit={onSubmit} noValidate className="space-y-4">
                <Field label={dict.materials.fields.name} required error={form.errors.name}><TextInput {...form.field("name")} error={form.errors.name} data-testid="mat-name"/></Field>
                <Field label={dict.materials.fields.unit}><SelectInput {...form.field("unit")} options={Object.entries(dict.materials.units).map(([k,v])=>({value:k,label:v}))} data-testid="mat-unit"/></Field>
                <Field label={dict.materials.fields.notes}><Textarea rows={2} {...form.field("notes")}/></Field>
            </form>
        </Modal>
        <ConfirmDialog open={!!confirm} title={confirm?.title} onCancel={()=>setConfirm(null)} onConfirm={()=>doArchive(confirm.id,confirm.archived)}/>
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

// ============================================================
// CHECKLISTS
// ============================================================

export function ChecklistsList() {
    const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); const [toast, setToast] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get("/photo-checklists"); setItems(r.data); }
        catch (e) { setToast({msg:formatApiError(e),type:"error"}); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    return <div data-testid="checklists-page" className="space-y-5">
        <header><h1 className="font-display text-4xl sm:text-5xl font-extrabold">{dict.checklists.title}</h1><p className="mt-2 text-slate-400 text-sm">{dict.checklists.subtitle}</p></header>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="checklists-table">
                <thead className="bg-slate-900/80 text-xs uppercase text-slate-400"><tr><th className="text-left px-4 py-3">{dict.checklists.cols.subtype}</th><th className="text-left px-4 py-3">{dict.checklists.cols.baseCount}</th><th className="text-left px-4 py-3">{dict.checklists.cols.extraCount}</th><th className="text-left px-4 py-3">{dict.checklists.cols.total}</th><th className="text-right px-4 py-3">{dict.common.actions}</th></tr></thead>
                <tbody>
                    {loading && <tr><td colSpan={5} className="px-4 py-10 text-center"><Loader2 className="animate-spin inline"/></td></tr>}
                    {!loading && items.length===0 && <tr><td colSpan={5} className="px-0 py-0"><EmptyState icon={ClipboardList} title={dict.checklists.empty} testid="checklists-empty"/></td></tr>}
                    {!loading && items.map(c => {
                        const base = c.entries.filter(e=>e.is_base).length;
                        const extra = c.entries.length - base;
                        return <tr key={c.id} data-testid={`checklist-row-${c.id}`} className="border-t border-slate-800 hover:bg-slate-900/60">
                            <td className="px-4 py-3 text-slate-100">{c.asset_subtype}</td>
                            <td className="px-4 py-3 text-slate-300">{base}</td>
                            <td className="px-4 py-3 text-slate-300">{extra}</td>
                            <td className="px-4 py-3 text-slate-300">{c.entries.length}</td>
                            <td className="px-4 py-3"><div className="flex justify-end"><button onClick={()=>setEditing(c)} data-testid={`edit-checklist-${c.id}`} className="p-2 rounded hover:bg-slate-800"><Pencil size={16}/></button></div></td>
                        </tr>;
                    })}
                </tbody>
            </table>
        </div>
        {editing && <ChecklistEditor config={editing} onClose={()=>setEditing(null)} onSaved={() => { setEditing(null); load(); setToast({msg:dict.common.saved,type:"success"}); }}/>}
        {toast && <Toast {...toast} onClose={()=>setToast(null)}/>}
    </div>;
}

function ChecklistEditor({ config, onClose, onSaved }) {
    const baseEntries = config.entries.filter(e => e.is_base);
    const [extras, setExtras] = useState(config.entries.filter(e => !e.is_base).map(e => ({...e})));
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const addEntry = () => setExtras([...extras, {key:"",label_es:"",required:true}]);
    const updEntry = (i, patch) => setExtras(extras.map((e,j) => j===i?{...e,...patch}:e));
    const rmEntry = (i) => setExtras(extras.filter((_,j) => j!==i));

    const save = async () => {
        setError(null);
        for (const e of extras) {
            if (!e.key.trim() || !e.label_es.trim()) { setError(dict.validation.required); return; }
        }
        setSubmitting(true);
        try { await api.patch(`/photo-checklists/${config.id}`, { extra_entries: extras }); onSaved(); }
        catch (err) { setError(formatApiError(err)); }
        finally { setSubmitting(false); }
    };

    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="checklist-editor">
        <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
        <div className="relative w-full max-w-2xl rounded-2xl bg-slate-900 border border-slate-800 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
                <h2 className="font-display text-xl font-bold">{dict.checklists.editConfig} — {config.asset_subtype}</h2>
                <button onClick={onClose} className="text-slate-400 p-1"><X size={18}/></button>
            </div>
            <div className="overflow-y-auto p-5 space-y-5">
                <div>
                    <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2"><Lock size={12}/>{dict.checklists.sections.base}</h3>
                    <p className="text-xs text-slate-500 mb-3">{dict.checklists.baseTooltip}</p>
                    <ul className="space-y-1.5" data-testid="checklist-base">
                        {baseEntries.map(e => <li key={e.key} className="flex items-center gap-3 px-3 py-2 rounded bg-slate-950/60 border border-slate-800 text-slate-400">
                            <Lock size={12}/><span className="text-sm flex-1">{e.label_es}</span><span className="text-[10px] uppercase tracking-wider text-slate-600 font-mono">{e.key}</span></li>)}
                    </ul>
                </div>
                <div>
                    <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-3">{dict.checklists.sections.extras}</h3>
                    <ul className="space-y-2" data-testid="checklist-extras">
                        {extras.map((e,i) => <li key={i} className="grid grid-cols-12 gap-2 items-center">
                            <input value={e.key} onChange={ev=>updEntry(i,{key:ev.target.value})} placeholder={dict.checklists.fields.key} data-testid={`extra-key-${i}`} className="col-span-3 h-9 px-2 rounded bg-slate-950 border border-slate-700 text-sm"/>
                            <input value={e.label_es} onChange={ev=>updEntry(i,{label_es:ev.target.value})} placeholder={dict.checklists.fields.label} data-testid={`extra-label-${i}`} className="col-span-6 h-9 px-2 rounded bg-slate-950 border border-slate-700 text-sm"/>
                            <label className="col-span-2 flex items-center gap-1 text-xs"><input type="checkbox" checked={e.required} onChange={ev=>updEntry(i,{required:ev.target.checked})}/>{dict.checklists.fields.required}</label>
                            <button onClick={()=>rmEntry(i)} data-testid={`extra-remove-${i}`} className="col-span-1 text-red-400 p-2"><Trash2 size={14}/></button>
                        </li>)}
                    </ul>
                    <button onClick={addEntry} data-testid="extra-add" className="mt-3 inline-flex items-center gap-1 text-sm text-brand-orange hover:text-orange-400"><Plus size={14}/>{dict.checklists.addEntry}</button>
                </div>
                {error && <div className="text-sm text-red-300 bg-red-900/40 border border-red-800 rounded px-3 py-2" data-testid="checklist-error">{error}</div>}
            </div>
            <div className="px-5 py-4 border-t border-slate-800 flex justify-end gap-2">
                <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-700 text-sm">{dict.common.cancel}</button>
                <button onClick={save} disabled={submitting} data-testid="checklist-save" className="h-10 px-4 rounded-lg bg-brand-orange text-slate-950 font-semibold uppercase text-sm inline-flex items-center gap-2 disabled:opacity-60">{submitting&&<Loader2 className="animate-spin" size={14}/>}{submitting?dict.common.saving:dict.common.save}</button>
            </div>
        </div>
    </div>;
}
